import React, { useEffect, useState, useCallback, useRef } from "react";

import {
  Box,
  Card,
  Stack,
  Typography,
  CircularProgress,
  Backdrop,
  Paper,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import EditIcon from "@mui/icons-material/Edit";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import DownloadIcon from "@mui/icons-material/Download";

import SidebarView, { SidebarRef } from "../common/Sidebar";
import Headerbar from "../common/Headerbar";
import VideoPlayer from "./components/VideoPlayer";
import Subtitle from "./components/Subtitle";
import { AspectRatio, ASPECT_RATIOS } from "./components/AspectRatioSelector";
import { editShortVideoService } from "./services";
import { episodeService } from "../Episode/services";
import { Paragraph, SubtitleStyles } from "./types";
import Timeline from "../Editor/Timeline";
import { IElement } from "../Editor/Timeline/components/Element/types/IElement";
import { WebSocketEventName } from "../common/enums/WebSocketEventName";
import { useWebSocket } from "../../hooks/useWebSocket";
import Breadcrumbs from "../common/Breadcrumbs";

interface VideoControls {
  play: () => void;
  pause: () => void;
  seekTo: (time: number) => void;
  getDuration: () => number;
}

export default function EditShortVideo() {
  const { videoId, id: episodeId } = useParams();
  const sidebarRef = useRef<SidebarRef>(null);

  const { data: shortVideo } = useQuery({
    queryKey: ["getVideo", videoId],
    queryFn: () => editShortVideoService.getVideo(videoId),
  });

  // Fetch episode data for breadcrumbs
  const { data: episode } = useQuery({
    queryKey: ["getEpisodeById", episodeId],
    queryFn: () => episodeService.getEpisodeById(episodeId),
    enabled: !!episodeId,
  });

  const episodeName = episode?.name || "Episode";
  const videoTitle = shortVideo?.title || "Video";

  const [elements, setElements] = useState<IElement[]>([]);
  const [currentTimeInMilliseconds, setCurrentTimeInMilliseconds] =
    useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [styles, setStyles] = useState<SubtitleStyles>(
    shortVideo?.subtitle_styles ?? {},
  );
  const [paragraphs, setParagraphs] = useState<Paragraph[]>([]);
  const [videoControlsRef, setVideoControlsRef] =
    useState<VideoControls | null>(null);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    ASPECT_RATIOS.find((r) => r.id === "standard") || ASPECT_RATIOS[0], // Default to Standard
  );

  const socket = useWebSocket();

  const handleMobileMenuToggle = () => {
    sidebarRef.current?.toggleMobile();
  };

  const handleTimeUpdate = useCallback((timeInMs: number) => {
    setCurrentTimeInMilliseconds(timeInMs);
  }, []);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      videoControlsRef?.pause();
      setIsPlaying(false);
    } else {
      videoControlsRef?.play();
      setIsPlaying(true);
    }
  }, [isPlaying, videoControlsRef]);

  const handleSeek = useCallback(
    (timeInMs: number) => {
      if (videoControlsRef) {
        videoControlsRef.seekTo(timeInMs / 1000); // Convert to seconds for seekTo
        setCurrentTimeInMilliseconds(timeInMs);
      }
    },
    [videoControlsRef],
  );

  const handleAspectRatioChange = useCallback((newAspectRatio: AspectRatio) => {
    setAspectRatio(newAspectRatio);
  }, []);

  const handleVideoEnd = useCallback(() => {
    // When video ends, update the playing state to false
    setIsPlaying(false);
  }, []);

  // WebSocket effect for video export done event
  useEffect(() => {
    if (socket) {
      socket.on(WebSocketEventName.VIDEO_EXPORT_DONE, async (data) => {
        setIsExporting(false);

        if (data?.exported_video_url) {
          const fileResponse = await fetch(data.exported_video_url);
          const blob = await fileResponse.blob();
          const a = document.createElement("a");
          a.href = window.URL.createObjectURL(blob);
          a.download = `${data?.short_video?.title}.mp4`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      });
    }

    return () => {
      if (socket) {
        socket.off(WebSocketEventName.VIDEO_EXPORT_DONE);
      }
    };
  }, [socket]);

  // Pause video when export starts
  useEffect(() => {
    if (isExporting && isPlaying) {
      videoControlsRef?.pause();
      setIsPlaying(false);
    }
  }, [isExporting, isPlaying, videoControlsRef]);

  // Initialize elements when video loads
  useEffect(() => {
    if (shortVideo) {
      setStyles(shortVideo.subtitle_styles);
      setParagraphs(shortVideo?.paragraphs ?? []);
      setElements([
        {
          id: 1,
          row: 1,
          start: 0,
          width: shortVideo.duration_in_milliseconds,
          isSelected: false,
          isHovered: false,
          isDragging: false,
          isResizing: false,
        },
      ]);
    }
  }, [shortVideo]);

  if (!shortVideo) {
    return <Typography>Loading...</Typography>;
  }

  const handleUpdateStyles = async (
    updatedStyles: SubtitleStyles,
  ): Promise<void> => {
    if (videoId) {
      await editShortVideoService.updateShortVideo(videoId, {
        subtitleStyles: updatedStyles,
      });
      setStyles(updatedStyles);
    }
  };

  const handleUpdateParagraph = async (paragraph: Paragraph): Promise<void> => {
    if (videoId) {
      setParagraphs(
        paragraphs.map((p) => (p.id === paragraph.id ? paragraph : p)),
      );
      await editShortVideoService.updateShortVideo(videoId, {
        paragraph,
      });
    }
  };

  if (!videoId) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <SidebarView
        pageName="Edit Short Video"
        expended={false}
        editFileSidebar
        hidePrimary
        ref={sidebarRef}
      />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          minWidth: 0, // Prevents flex item from overflowing
          overflow: "hidden",
        }}
      >
        <Headerbar
          pageName={`${videoTitle}`}
          onMobileMenuClick={handleMobileMenuToggle}
          showLogo
        />
        <Breadcrumbs
          items={[
            {
              label: "Episodes",
              path: "/episodes",
              icon: <PlaylistPlayIcon sx={{ fontSize: 18 }} />,
            },
            {
              label: episodeName,
              path: `/episodes/${episodeId}`,
            },
            {
              label: "Short Videos",
              path: `/episodes/${episodeId}/videos`,
              icon: <OndemandVideoIcon sx={{ fontSize: 18 }} />,
            },
            {
              label: videoTitle,
              path: `/episodes/${episodeId}/videos/${videoId}/edit`,
              icon: <EditIcon sx={{ fontSize: 18 }} />,
            },
          ]}
          showHome={false}
        />
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.grey[50],
            flex: 1,
            overflow: "hidden",
            minWidth: 0, // Prevents flex item from overflowing
          }}
        >
          <Card
            component={Stack}
            spacing={0}
            direction="column"
            sx={{
              position: "relative",
              px: 0,
              py: 1,
              height: "100%",
              backgroundColor: (theme) => theme.palette.grey[50],
              minWidth: 0, // Prevents flex item from overflowing
            }}
          >
            <Backdrop
              open={isExporting}
              sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              }}
            >
              <Paper
                elevation={24}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  backgroundColor: "white",
                  maxWidth: 400,
                  width: "90%",
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                  <VideoFileIcon
                    sx={{
                      position: "absolute",
                      fontSize: 24,
                      color: (theme) => theme.palette.primary.main,
                    }}
                  />
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: (theme) => theme.palette.text.primary,
                      mb: 1,
                    }}
                  >
                    Exporting Video
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      lineHeight: 1.5,
                    }}
                  >
                    Your video is being processed and exported. This may take a
                    few minutes depending on the video length and quality.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: "0.875rem",
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption">
                    The file will download automatically when ready
                  </Typography>
                </Box>
              </Paper>
            </Backdrop>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
                minWidth: 0, // Prevents flex item from overflowing
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  maxHeight: "calc(100% - 120px)", // Reduced from 250px to 120px to account for smaller timeline
                  px: 2,
                  minWidth: 0, // Prevents flex item from overflowing
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{ width: "30%", px: 2, minWidth: 0, overflow: "hidden" }}
                >
                  <Subtitle
                    styles={styles}
                    paragraphs={paragraphs}
                    onUpdateStyles={handleUpdateStyles}
                    onUpdateParagraph={handleUpdateParagraph}
                    currentTimeInMilliseconds={currentTimeInMilliseconds}
                    onParagraphClick={handleSeek}
                  />
                </Box>
                <Box
                  sx={{
                    width: "70%",
                    alignSelf: "start",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0, // Prevents flex item from overflowing
                    overflow: "hidden",
                  }}
                >
                  <VideoPlayer
                    videoId={videoId}
                    url={shortVideo?.url ?? ""}
                    paragraphs={paragraphs}
                    isPlaying={isPlaying}
                    toggleIsPlaying={handlePlayPause}
                    subtitleStyles={styles}
                    onTimeUpdate={handleTimeUpdate}
                    currentTimeInMilliseconds={currentTimeInMilliseconds}
                    onControlsReady={setVideoControlsRef}
                    onUpdateStyles={handleUpdateStyles}
                    aspectRatio={aspectRatio}
                    onVideoEnd={handleVideoEnd}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  width: "100%",
                  alignSelf: "center",
                }}
              >
                <Timeline
                  video={shortVideo}
                  currentTimeInMilliseconds={currentTimeInMilliseconds}
                  isPlaying={isPlaying}
                  onPlayPause={handlePlayPause}
                  setCurrentTimeInMilliseconds={handleSeek}
                  elements={elements}
                  setElements={setElements}
                  setIsExporting={setIsExporting}
                  currentAspectRatio={aspectRatio}
                  onAspectRatioChange={handleAspectRatioChange}
                />
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
